import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import './PropertyImageDisplay.scss';

interface PropertyImage {
  id?: string;
  url: string;
  thumbnailUrl?: string;
  enhancedUrl?: string;
  isEnhanced?: boolean;
}

interface Props {
  images: PropertyImage[];
  maxDisplay?: number;
}

const PropertyImageDisplay: React.FC<Props> = ({ images, maxDisplay = 9 }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  if (!images || images.length === 0) {
    return null;
  }
  
  const displayImages = images.slice(0, maxDisplay);
  const remainingCount = images.length - maxDisplay;
  
  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setShowLightbox(true);
  };
  
  const handlePrevious = () => {
    setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };
  
  const handleNext = () => {
    setSelectedImageIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };
  
  return (
    <>
      <div className="property-image-display">
        <div className="image-grid">
          {displayImages.map((image, index) => (
            <div
              key={image.id || index}
              className={`image-item ${index === maxDisplay - 1 && remainingCount > 0 ? 'last-item' : ''}`}
              onClick={() => handleImageClick(index)}
            >
              <img
                src={image.thumbnailUrl || image.url}
                alt={`Property ${index + 1}`}
                loading="lazy"
              />
              {image.isEnhanced && (
                <span className="enhanced-badge">AI Enhanced</span>
              )}
              {index === maxDisplay - 1 && remainingCount > 0 && (
                <div className="overlay">
                  <span className="remaining-count">+{remainingCount} more</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Lightbox Modal */}
      <Modal
        show={showLightbox}
        onHide={() => setShowLightbox(false)}
        size="xl"
        centered
        className="image-lightbox"
      >
        <Modal.Body>
          <button
            className="lightbox-close"
            onClick={() => setShowLightbox(false)}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
          
          {images.length > 1 && (
            <>
              <button
                className="lightbox-nav lightbox-prev"
                onClick={handlePrevious}
                aria-label="Previous"
              >
                <span>‹</span>
              </button>
              <button
                className="lightbox-nav lightbox-next"
                onClick={handleNext}
                aria-label="Next"
              >
                <span>›</span>
              </button>
            </>
          )}
          
          <img
            src={images[selectedImageIndex]?.url}
            alt={`Property ${selectedImageIndex + 1}`}
            className="lightbox-image"
          />
          
          {images.length > 1 && (
            <div className="image-counter">
              {selectedImageIndex + 1} / {images.length}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PropertyImageDisplay;

